import React, { ClipboardEvent, KeyboardEvent } from 'react';

import { FormField } from '@monorepo/common';

import { hasLineBreak, replaceLineBreaks, SHORT_DESCRIPTION_MAX_LENGTH } from '../../utils/shortDescription';

type FormFieldProps = React.ComponentProps<typeof FormField>;

interface IShortDescriptionFieldProps {
  extraProps: FormFieldProps['extraProps'];
  errors: FormFieldProps['errors'];
}

// The Short Description is one line. Enter is blocked before the browser acts on it, so a
// selection survives and undo keeps working. Enter that confirms an IME composition is left
// alone.
const blockEnter = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
  if (event.target instanceof HTMLTextAreaElement) event.preventDefault();
};

// A pasted line break becomes a space. insertText goes through the browser's own editing, so
// maxlength, undo and React's onChange all behave as if the user had typed it. Where
// insertText is not supported, the text is spliced in and an input event tells React.
// A dropped line break is not caught here; the schema's one-line rule reports it.
const pasteOnOneLine = (event: ClipboardEvent<HTMLElement>) => {
  const field = event.target;
  const text = event.clipboardData?.getData('text') || '';
  if (!(field instanceof HTMLTextAreaElement) || !hasLineBreak(text)) return;
  event.preventDefault();
  const clean = replaceLineBreaks(text);
  if (typeof document.execCommand === 'function' && document.execCommand('insertText', false, clean)) return;
  const { selectionStart, selectionEnd } = field;
  const available = field.maxLength - (field.value.length - (selectionEnd - selectionStart));
  const replacement = clean.slice(0, Math.max(0, available));
  // Do not leave half of a surrogate pair when the final slot falls inside a Unicode character.
  const safeReplacement = /[\uD800-\uDBFF]$/.test(replacement) ? replacement.slice(0, -1) : replacement;
  field.setRangeText(safeReplacement, selectionStart, selectionEnd, 'end');
  field.dispatchEvent(new Event('input', { bubbles: true }));
};

export const ShortDescriptionField = ({ extraProps, errors }: IShortDescriptionFieldProps) => (
  <div
    onKeyDownCapture={blockEnter}
    onPasteCapture={pasteOnOneLine}
  >
    <FormField
      isRequired
      type="textarea"
      textAreaRow={9}
      maxLength={SHORT_DESCRIPTION_MAX_LENGTH}
      extraProps={extraProps}
      errors={errors}
      label="Short Description"
    />
  </div>
);
